import { AppState, Auth0Provider } from "@auth0/auth0-react";
import { router } from "App";
import React, { ReactNode } from "react";

// How long the SDK waits for the hidden /authorize iframe that backs up a failed
// refresh-token exchange. The iframe only reports back via postMessage, so when
// Auth0 answers with an error page instead (e.g. a 400) the SDK sits out the
// whole timeout — 60s by default — before rejecting with `timeout`. Until then
// the agent sees nothing but the boot spinner.
const SILENT_AUTH_TIMEOUT_IN_SECONDS = 10;

const Auth0ProviderWithHistory = ({ children }: { children: ReactNode }) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

  const onRedirectCallback = (appState: AppState | undefined) => {
    // Client-side navigation via the module-level router (this provider sits
    // above RouterProvider, so useNavigate isn't available here). Replaces the
    // former `window.location.href = returnTo`, which hard-reloaded the whole
    // SPA — a second cold boot on every fresh login. `returnTo` is preserved so
    // deep-link-after-login still works; the ?code&state params are dropped by
    // the replace navigation.
    router.navigate(appState?.returnTo || "/", { replace: true });
  };

  const shouldSkipRedirectCallback =
    window.location.pathname.includes("/quickbooks") ||
    window.location.pathname.includes("/xero") ||
    window.location.pathname.includes("/reviews/google/callback") ||
    window.location.pathname.includes("/google/gmail/callback") ||
    window.location.pathname.includes("/microsoft/callback") ||
    window.location.pathname.includes("/facebook/callback/reviews") ||
    window.location.pathname.includes("/facebook/callback/messenger") ||
    window.location.pathname.includes("/facebook/callback/instagram");

  return (
    <Auth0Provider
      useRefreshTokens
      cacheLocation="localstorage"
      domain={domain as string}
      clientId={clientId as string}
      authorizationParams={{
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        redirect_uri: `${window.location.origin}/inbox`,
        grant_type: "refresh_token",
      }}
      onRedirectCallback={onRedirectCallback}
      skipRedirectCallback={shouldSkipRedirectCallback}
      useRefreshTokensFallback={true}
      authorizeTimeoutInSeconds={SILENT_AUTH_TIMEOUT_IN_SECONDS}
    >
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory;
